import React, { useState, useEffect } from 'react';
import { useDashboardTranslation } from '../../i18n';
import {
  Loader2, Activity, CheckCircle, Globe, Plus, X, Settings2, FileJson,
  AlertTriangle, Search, Key, ExternalLink, BookOpen,
} from 'lucide-react';
import type { WizardFormData } from './types';
import type { AllowedInternalRoutes } from '../../types';
import {
  INDICATORS_CATALOG, INDICATOR_PAISES, INDICATOR_CATEGORIAS,
  buildIndicatorUrl, type EconomicIndicator,
} from '../../utils/indicatorsCatalog';

const HIGH_VOLUME_THRESHOLD = 1000;

interface Step1DataSourceProps {
  formData: WizardFormData;
  testSuccess: boolean;
  isTesting: boolean;
  testError: string | null;
  totalRecords?: number;
  allowedRoutes?: AllowedInternalRoutes;
  isAdmin?: boolean;
  showExternalEndpoints?: boolean;
  onUpdateForm: (key: keyof WizardFormData, value: unknown) => void;
  onTestConnection: () => void;
  onIndicatorSelect: (indicator: EconomicIndicator | null) => void;
}

export const Step1DataSource: React.FC<Step1DataSourceProps> = ({
  formData,
  testSuccess,
  isTesting,
  testError,
  totalRecords,
  onUpdateForm,
  onTestConnection,
  onIndicatorSelect,
}) => {
  const { t } = useDashboardTranslation();
  // ── Estado de catálogo ──────────────────────────────────────
  const [filtroPais, setFiltroPais] = useState<string>('Todos');
  const [filtroCateg, setFiltroCateg] = useState<string>('Todas');
  const [busqueda, setBusqueda] = useState('');

  // ── Estado de modo manual (avanzado) ───────────────────────
  const [newHeaderKey, setNewHeaderKey] = useState('');
  const [newHeaderValue, setNewHeaderValue] = useState('');
  const headers = formData.endpoint_headers ?? {};
  const headerCount = Object.keys(headers).length;
  const hasParams = Object.keys(formData.endpoint_params ?? {}).length > 0;
  const [showAdvanced, setShowAdvanced] = useState(headerCount > 0 || hasParams);
  const [bodyJsonText, setBodyJsonText] = useState(() =>
    hasParams ? JSON.stringify(formData.endpoint_params, null, 2) : ''
  );
  const [bodyJsonError, setBodyJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (hasParams) setBodyJsonText(JSON.stringify(formData.endpoint_params, null, 2));
  }, [formData.endpoint_params, hasParams]);

  // ── Indicador seleccionado ──────────────────────────────────
  const selectedIndicator = INDICATORS_CATALOG.find(i => i.id === formData.catalog_indicator_id) ?? null;

  const catalogMode = formData.catalog_mode;

  const handleModeChange = (mode: 'catalogo' | 'manual') => {
    onUpdateForm('catalog_mode', mode);
    onIndicatorSelect(null);
    onUpdateForm('endpoint_url', '');
    onUpdateForm('catalog_indicator_id', undefined);
    onUpdateForm('campo_valor', '');
    onUpdateForm('campo_fecha', '');
  };

  const handleIndicatorClick = (ind: EconomicIndicator) => {
    const isAlreadySelected = formData.catalog_indicator_id === ind.id;
    if (isAlreadySelected) {
      // Deseleccionar
      onUpdateForm('catalog_indicator_id', undefined);
      onUpdateForm('endpoint_url', '');
      onUpdateForm('campo_valor', '');
      onUpdateForm('campo_fecha', '');
      // Al deseleccionar se deshace también: si no, quedaría marcado externo con la URL vacía y el
      // siguiente paso manual heredaría una decisión que ya no viene de ningún sitio.
      onUpdateForm('is_internal', undefined);
      onIndicatorSelect(null);
    } else {
      onUpdateForm('catalog_indicator_id', ind.id);
      onUpdateForm('endpoint_url', buildIndicatorUrl(ind, formData.catalog_api_key || undefined));
      onUpdateForm('endpoint_method', ind.metodo);
      onUpdateForm('campo_valor', ind.campo_valor);
      onUpdateForm('campo_fecha', ind.campo_fecha);
      onUpdateForm('catalog_api_key', formData.catalog_api_key);
      // 🔴 Un indicador del catálogo es EXTERNO por definición —todos apuntan a una API pública— y
      // hay que decirlo AQUÍ, al elegirlo.
      //
      // Antes no se decía en ninguna parte de este camino: `is_internal` solo se calculaba dentro de
      // `handleTestConnection`, y en modo catálogo no hay que probar para avanzar —correcto, el
      // indicador ya viene verificado—. Así que la detección nunca corría, al guardar caía a
      // `?? true`, y el widget nacía declarado INTERNO apuntando a `www.datos.gov.co`. El error no
      // aparecía al crearlo sino al pintarlo: «Host no está en la lista blanca de hosts internos».
      //
      // ⚠️ Era un dato que se calculaba como efecto secundario de otra acción, y desaparecía cuando
      // esa acción no ocurría. Por eso se pone al elegir y no al probar.
      onUpdateForm('is_internal', false);
      // Pre-rellenar nombre del widget con el del indicador
      if (!formData.nombre) onUpdateForm('nombre', ind.nombre);
      onIndicatorSelect(ind);
    }
  };

  const handleApiKeyChange = (key: string) => {
    onUpdateForm('catalog_api_key', key);
    // Actualizar la URL del indicador seleccionado con la nueva key
    if (selectedIndicator) {
      onUpdateForm('endpoint_url', buildIndicatorUrl(selectedIndicator, key || undefined));
    }
  };

  const filteredCatalog = INDICATORS_CATALOG.filter(i =>
    (filtroPais === 'Todos' || i.pais === filtroPais) &&
    (filtroCateg === 'Todas' || i.categoria === filtroCateg) &&
    (busqueda === '' || i.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
     i.descripcion.toLowerCase().includes(busqueda.toLowerCase()))
  );

  // ── Handlers modo manual ────────────────────────────────────
  const addHeader = () => {
    const key = newHeaderKey.trim();
    if (!key) return;
    onUpdateForm('endpoint_headers', { ...headers, [key]: newHeaderValue });
    setNewHeaderKey('');
    setNewHeaderValue('');
  };

  const handleBodyJsonChange = (text: string) => {
    setBodyJsonText(text);
    if (!text.trim()) { onUpdateForm('endpoint_params', {}); setBodyJsonError(null); return; }
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setBodyJsonError('Debe ser un objeto JSON (no array ni primitivo)'); return;
      }
      onUpdateForm('endpoint_params', parsed); setBodyJsonError(null);
    } catch { setBodyJsonError('JSON inválido'); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-800">{t('dashboard.builder.step1.title')}</h3>
        <p className="text-sm text-gray-600 mt-0.5">{t('dashboard.builder.step1.subtitle')}</p>
      </div>

      {/* ── Toggle modo ─────────────────────────────────────── */}
      <div className="flex bg-gray-100 p-1 rounded-lg w-full gap-1">
        <button
          onClick={() => handleModeChange('catalogo')}
          className={`flex-1 flex flex-col items-center px-4 py-1.5 rounded-md text-xs font-medium transition-all ${
            catalogMode === 'catalogo' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="font-semibold">{t('dashboard.builder.step1.catalog')}</span>
          <span className={`text-[10px] font-semibold ${catalogMode === 'catalogo' ? 'text-blue-600' : 'text-gray-400'}`}>
            Recomendado
          </span>
        </button>
        <button
          onClick={() => handleModeChange('manual')}
          className={`flex-1 flex flex-col items-center px-4 py-1.5 rounded-md text-xs font-medium transition-all ${
            catalogMode === 'manual' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="font-semibold">{t('dashboard.builder.step1.manual')}</span>
          <span className={`text-[10px] font-semibold ${catalogMode === 'manual' ? 'text-blue-600' : 'text-gray-400'}`}>
            Avanzado
          </span>
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════ */}
      {/* MODO CATÁLOGO                                         */}
      {/* ══════════════════════════════════════════════════════ */}
      {catalogMode === 'catalogo' && (
        <div className="space-y-3">
          {/* Buscador */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('dashboard.builder.step1.searchIndicator')}
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Filtros por país */}
          <div className="flex flex-wrap gap-1.5">
            {INDICATOR_PAISES.map(p => (
              <button
                key={p}
                onClick={() => setFiltroPais(p)}
                className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                  filtroPais === p
                    ? 'bg-gray-800 text-white border-gray-800'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Filtros por categoría */}
          <div className="flex flex-wrap gap-1.5">
            {INDICATOR_CATEGORIAS.map(c => (
              <button
                key={c}
                onClick={() => setFiltroCateg(c)}
                className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                  filtroCateg === c
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Lista de indicadores */}
          <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto pr-1">
            {filteredCatalog.map(ind => {
              const isSelected = formData.catalog_indicator_id === ind.id;
              return (
                <button
                  key={ind.id}
                  onClick={() => handleIndicatorClick(ind)}
                  className={`w-full text-left rounded-lg px-3 py-2.5 border transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50/50 shadow-[0_0_0_3px_#DBEAFE]'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/20'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="text-base flex-shrink-0">{ind.bandera}</span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-sm font-semibold truncate ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                            {ind.nombre}
                          </span>
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">
                            {ind.categoria}
                          </span>
                          {ind.requiere_key && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                              <Key className="w-2.5 h-2.5" /> Key
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {ind.descripcion} · {ind.frecuencia} · {ind.unidad}
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
            {filteredCatalog.length === 0 && (
              <div className="text-center text-gray-400 text-sm py-8">
                <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                Sin resultados para esa búsqueda
              </div>
            )}
          </div>

          {/* Input API Key si el indicador la requiere */}
          {selectedIndicator?.requiere_key && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
              <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                <Key className="w-3.5 h-3.5" /> {selectedIndicator.key_nombre} requerida
              </p>
              <input
                type="text"
                placeholder={`Pega aquí tu ${selectedIndicator.key_nombre}...`}
                value={formData.catalog_api_key}
                onChange={e => handleApiKeyChange(e.target.value)}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
              />
              {selectedIndicator.key_url && (
                <a
                  href={selectedIndicator.key_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Obtener gratis → {selectedIndicator.key_url.replace('https://', '')}
                </a>
              )}
            </div>
          )}

          {/* Info banner — el catálogo no requiere probar conexión */}
          {selectedIndicator && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
              <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800">
                <span className="font-semibold">{t('dashboard.builder.step1.ready')}</span>
                <span className="text-blue-600"> — tipo y mapeo se configuran automáticamente. No necesitas probar la conexión.</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════ */}
      {/* MODO MANUAL                                           */}
      {/* ══════════════════════════════════════════════════════ */}
      {catalogMode === 'manual' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50/50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <Globe className="w-4 h-4 flex-shrink-0" />
            <span>{t('dashboard.builder.step1.external')}</span>
          </div>

          <div className="flex gap-2">
            <div className="w-24 flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('dashboard.builder.step1.method')}</label>
              <select
                value={formData.endpoint_method}
                onChange={e => onUpdateForm('endpoint_method', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('dashboard.builder.step1.url')}</label>
              <input
                type="url"
                placeholder="https://api.externa.com/v1/datos"
                value={formData.endpoint_url}
                onChange={e => onUpdateForm('endpoint_url', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end items-center gap-2">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`relative p-2 border rounded-lg transition-colors ${
                showAdvanced || headerCount > 0 || hasParams
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
              }`}
              title="Headers y Body"
            >
              <Settings2 className="w-4 h-4" />
              {(headerCount > 0 || hasParams) && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {headerCount + (hasParams ? 1 : 0)}
                </span>
              )}
            </button>
            <button
              onClick={onTestConnection}
              disabled={!formData.endpoint_url || isTesting}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
              Probar Conexión
            </button>
          </div>

          {showAdvanced && (
            <div className="border-t border-gray-200 pt-3 space-y-4">
              <div className="space-y-2.5">
                <label className="text-sm font-medium text-gray-700">{t('dashboard.builder.step1.customHeaders')}</label>
                {Object.entries(headers).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <input type="text" value={key} disabled className="w-1/3 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-gray-50 font-mono" />
                    <input type="text" value={value} onChange={e => onUpdateForm('endpoint_headers', { ...headers, [key]: e.target.value })} className="flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-blue-500" />
                    <button onClick={() => { const n = { ...headers }; delete n[key]; onUpdateForm('endpoint_headers', n); }} className="p-1.5 text-gray-400 hover:text-red-600 rounded-md"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <div className="flex items-center gap-1.5">
                  <input type="text" value={newHeaderKey} onChange={e => setNewHeaderKey(e.target.value)} onKeyDown={e => e.key === 'Enter' && addHeader()} placeholder="Authorization" className="w-1/3 px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-blue-500 font-mono" />
                  <input type="text" value={newHeaderValue} onChange={e => setNewHeaderValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && addHeader()} placeholder="Bearer abc123..." className="flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-blue-500" />
                  <button onClick={addHeader} disabled={!newHeaderKey.trim()} className="flex items-center gap-1 px-2 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-xs font-medium hover:bg-blue-100 disabled:opacity-50">
                    <Plus className="w-3 h-3" /> Agregar
                  </button>
                </div>
              </div>

              {formData.endpoint_method === 'POST' && (
                <div className="space-y-1 pt-3 border-t border-gray-100">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                    <FileJson className="w-3.5 h-3.5" /> Body JSON
                  </label>
                  <textarea
                    value={bodyJsonText}
                    onChange={e => handleBodyJsonChange(e.target.value)}
                    placeholder='{"from": "2026-01-01", "to": "2026-01-31"}'
                    rows={5}
                    className={`w-full p-2 border rounded-md text-xs font-mono focus:ring-2 ${bodyJsonError ? 'border-red-300 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-500'}`}
                  />
                  {bodyJsonError && <p className="text-xs text-red-600">{bodyJsonError}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Banners de estado (compartidos ambos modos) ────────── */}
      {testSuccess && (
        <div className="p-2.5 bg-green-50 text-green-800 text-sm rounded-lg border border-green-200 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          Conexión exitosa. El proxy devolvió un JSON válido.
        </div>
      )}
      {testError && (
        <div className="p-2.5 bg-red-50 text-red-800 text-sm rounded-lg border border-red-200 flex items-start gap-2">
          <Activity className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span><strong>{t('dashboard.builder.step1.error')}</strong> {testError}</span>
        </div>
      )}
      {testSuccess && totalRecords != null && totalRecords > HIGH_VOLUME_THRESHOLD && (
        <div className="p-2.5 bg-amber-50 text-amber-800 text-sm rounded-lg border border-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            <strong>{t('dashboard.builder.step1.performance')}</strong> Esta fuente devuelve <strong>{totalRecords.toLocaleString('es-CO')}</strong> registros.
            Considera usar una fuente que devuelva datos ya agrupados.
          </span>
        </div>
      )}
    </div>
  );
};
